import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { getInvoiceFromAppointment, getInvoice } from "../../core/api/invoiceService";
import { fetchPatients } from "../../core/redux/slices/patientSlice";
import { fetchDoctors } from "../../core/redux/slices/doctorSlice";
import { fetchProcedures } from "../../core/redux/slices/procedureSlice";
import { fetchProducts } from "../../core/redux/slices/productSlice";
import { createInvoiceThunk, updateInvoiceThunk } from "../../core/redux/slices/invoiceSlice";

const PAYMENT_MODES = [
  { value: "Cash", label: "Cash" },
  { value: "Card", label: "Card" },
  { value: "UPI", label: "UPI" },
  { value: "NetBanking", label: "Net Banking" },
  { value: "Cheque", label: "Cheque" },
];

const EMPTY_SERVICE = { procedureId: "", procedureName: "", quantity: 1, rate: 0, discount: 0, amount: 0 };
const EMPTY_PRODUCT = { productId: "", productName: "", quantity: 1, rate: 0, discount: 0, amount: 0 };
const EMPTY_PAYMENT = { paymentMode: "Cash", amount: 0, referenceNo: "", paymentDate: "" };

const calcLine = (qty, rate, discount) => {
  const base = Number(qty || 0) * Number(rate || 0);
  const disc = base * (Number(discount || 0) / 100);
  return Math.max(0, base - disc);
};

const round2 = (v) => Math.round(Number(v) * 100) / 100;
const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const today = () => new Date().toISOString().split("T")[0];

const InvoiceForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { state: navState } = useLocation();
  const appointmentId = searchParams.get("appointmentId");
  // Appointment data passed via navigate state from AppointmentModal
  const navAppointment = navState?.appointment || null;

  const patients = useSelector((s) => s.patients.list);
  const doctors = useSelector((s) => s.doctors.list);
  const procedures = useSelector((s) => s.procedures.list);
  const products = useSelector((s) => s.products.list);

  const isEdit = !!id;

  const [header, setHeader] = useState({
    invoiceDate: today(),
    patientId: "",
    patientName: "",
    doctorId: "",
    appointmentId: appointmentId || "",
    remarks: "",
  });

  const [services, setServices] = useState([{ ...EMPTY_SERVICE }]);
  const [productLines, setProductLines] = useState([]);
  const [payments, setPayments] = useState([{ ...EMPTY_PAYMENT, paymentDate: today() }]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  // Reference-only display (invoice #, status) — not used for calculations, since the
  // form now fully re-derives totals/balance from the editable line items and payments.
  const [invoiceMeta, setInvoiceMeta] = useState({ invoiceNumber: "", invoiceStatus: "" });

  const patientOptions = patients.map((p) => ({
    value: p.patientID || p.id,
    label: `${p.patientName} — ${p.mobileNumber || p.mobile || ""} (OP: ${p.opNumber || "—"})`,
    patientName: p.patientName,
  }));
  const doctorOptions = doctors.map((d) => ({ value: d.doctorID || d.id, label: d.doctorName }));
  const procedureOptions = procedures.map((p) => ({ value: p.procedureID || p.id, label: p.procedureName, rate: p.chargeNormal || 0, duration: p.defaultDuration || 30 }));
  const productOptions = products.map((p) => ({ value: p.productID || p.id, label: p.itemName, rate: p.sellingRate || 0 }));

  useEffect(() => {
    dispatch(fetchPatients());
    dispatch(fetchDoctors());
    dispatch(fetchProcedures());
    dispatch(fetchProducts());
  }, [dispatch]);

  // Pre-fill from appointment (data passed via navigate state takes priority over API)
  useEffect(() => {
    if (appointmentId && !isEdit) {
      // If appointment data was passed via navigate state, use it directly
      if (navAppointment) {
        setHeader((h) => ({
          ...h,
          patientId: navAppointment.patientId || "",
          patientName: navAppointment.patientName || "",
          doctorId: navAppointment.doctorId || "",
          appointmentId,
        }));
        if (navAppointment.procedureId) {
          setServices([{
            ...EMPTY_SERVICE,
            procedureId: navAppointment.procedureId,
            procedureName: navAppointment.procedureName || "",
          }]);
        }
        return;
      }
      // Fall back to API
      setLoading(true);
      getInvoiceFromAppointment(appointmentId)
        .then((res) => {
          const inv = res.data;
          if (!inv) return;
          setHeader((h) => ({
            ...h,
            patientId: inv.patientId || "",
            patientName: inv.patientName || "",
            doctorId: inv.doctorId || "",
            appointmentId,
          }));
          if (inv.services?.length) setServices(inv.services.map((s) => ({ ...EMPTY_SERVICE, ...s })));
          if (inv.products?.length) setProductLines(inv.products.map((p) => ({ ...EMPTY_PRODUCT, ...p })));
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [appointmentId, isEdit, navAppointment]);

  // Load existing invoice for edit.
  // Note: the API returns patientID/doctorID/appointmentID, procedureLines/productLines
  // (with procedureID/productID/qty), not the patientId/services/quantity names used
  // elsewhere in this form — those come from a completely different (create) payload shape.
  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      getInvoice(id)
        .then((res) => {
          const inv = res.data;
          setHeader({
            invoiceDate: (inv.invoiceDate || today()).split("T")[0],
            patientId: inv.patientID ?? inv.patientId ?? "",
            patientName: inv.patientName || "",
            doctorId: inv.doctorID ?? inv.doctorId ?? "",
            appointmentId: inv.appointmentID ?? inv.appointmentId ?? "",
            remarks: inv.remarks || "",
          });
          const procedureLines = inv.procedureLines || inv.services || [];
          setServices(procedureLines.length ? procedureLines.map((s) => ({
            ...EMPTY_SERVICE,
            procedureId: s.procedureID ?? s.procedureId ?? "",
            procedureName: s.procedureName || "",
            quantity: s.qty ?? s.quantity ?? 1,
            rate: s.rate ?? 0,
            discount: s.discount ?? 0,
            amount: s.amount ?? 0,
          })) : [{ ...EMPTY_SERVICE }]);
          const productLinesRaw = inv.productLines || inv.products || [];
          setProductLines(productLinesRaw.map((p) => ({
            ...EMPTY_PRODUCT,
            productId: p.productID ?? p.productId ?? "",
            productName: p.itemName || p.productName || "",
            quantity: p.qty ?? p.quantity ?? 1,
            rate: p.rate ?? 0,
            discount: p.discount ?? 0,
            amount: p.amount ?? 0,
          })));
          const existingPayments = (inv.payments || []).map((p) => ({
            paymentMode: p.paymentMode || "Cash",
            amount: Number(p.amount || 0),
            referenceNo: p.transactionRef || p.referenceNo || "",
            paymentDate: (p.paymentDate || today()).split("T")[0],
          }));
          setPayments(existingPayments.length ? existingPayments : [{ ...EMPTY_PAYMENT, paymentDate: today() }]);
          setInvoiceMeta({
            invoiceNumber: inv.invoiceNumber || "",
            invoiceStatus: inv.invoiceStatus || "",
          });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const serviceTotal = services.reduce((sum, s) => sum + calcLine(s.quantity, s.rate, s.discount), 0);
  const productTotal = productLines.reduce((sum, p) => sum + calcLine(p.quantity, p.rate, p.discount), 0);
  const grossTotal = serviceTotal + productTotal;
  const paidTotal = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const balance = round2(grossTotal - paidTotal);

  // Service line handlers
  const updateService = (idx, field, value) => {
    setServices((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      next[idx].amount = calcLine(next[idx].quantity, next[idx].rate, next[idx].discount);
      return next;
    });
  };
  const selectProcedure = (idx, opt) => {
    setServices((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], procedureId: opt?.value || "", procedureName: opt?.label || "", rate: opt?.rate || 0 };
      next[idx].amount = calcLine(next[idx].quantity, next[idx].rate, next[idx].discount);
      return next;
    });
  };
  const addService = () => setServices((prev) => [...prev, { ...EMPTY_SERVICE }]);
  const removeService = (idx) => setServices((prev) => prev.filter((_, i) => i !== idx));

  // Product line handlers
  const updateProduct = (idx, field, value) => {
    setProductLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      next[idx].amount = calcLine(next[idx].quantity, next[idx].rate, next[idx].discount);
      return next;
    });
  };
  const selectProduct = (idx, opt) => {
    setProductLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], productId: opt?.value || "", productName: opt?.label || "", rate: opt?.rate || 0 };
      next[idx].amount = calcLine(next[idx].quantity, next[idx].rate, next[idx].discount);
      return next;
    });
  };
  const addProduct = () => setProductLines((prev) => [...prev, { ...EMPTY_PRODUCT }]);
  const removeProduct = (idx) => setProductLines((prev) => prev.filter((_, i) => i !== idx));

  // Payment handlers — fully editable in both create and edit mode, so an existing
  // invoice's collected payments (amount/mode/date/reference) can be corrected, not
  // just appended to.
  const updatePayment = (idx, field, value) => {
    setPayments((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };
  const addPayment = () => setPayments((prev) => [...prev, { ...EMPTY_PAYMENT, paymentDate: today() }]);
  const removePayment = (idx) => setPayments((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!header.patientId) {
      Swal.fire("Required", "Please select a patient.", "warning");
      return;
    }
    const validServices = services.filter((s) => s.procedureId);
    const validProducts = productLines.filter((p) => p.productId);
    if (!validServices.length && !validProducts.length) {
      Swal.fire("Required", "Add at least one service or product.", "warning");
      return;
    }
    const payload = {
      ...header,
      services: validServices,
      products: validProducts,
      payments: payments.filter((p) => Number(p.amount) > 0),
      totalAmount: round2(grossTotal),
      paidAmount: round2(paidTotal),
      balanceAmount: balance,
      status: balance <= 0 ? "Paid" : paidTotal > 0 ? "PartialPaid" : "Unpaid",
    };
    setSaving(true);
    try {
      if (isEdit) {
        await dispatch(updateInvoiceThunk({ id, data: payload })).unwrap();
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Invoice updated", showConfirmButton: false, timer: 2000 });
      } else {
        await dispatch(createInvoiceThunk(payload)).unwrap();
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Invoice saved", showConfirmButton: false, timer: 2000 });
      }
      navigate("/invoices");
    } catch {
      Swal.fire("Error", isEdit ? "Failed to update invoice." : "Failed to save invoice.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>{isEdit ? "Edit Invoice" : "New Invoice"}</h4>
            <h6>{isEdit ? "Update invoice details and payments" : "Create patient invoice"}</h6>
          </div>
          <div className="page-btn d-flex gap-2">
            {isEdit && (
              <button type="button" className="btn btn-outline-primary" onClick={() => window.open(`/invoices/${id}/print`, "_blank")}>
                Print
              </button>
            )}
            <button className="btn btn-cancel" onClick={() => navigate("/invoices")}>Back to List</button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Invoice Details</h5>
              {isEdit && <span className="badge bg-secondary">{invoiceMeta.invoiceNumber} — {invoiceMeta.invoiceStatus}</span>}
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-5 mb-3">
                  <label className="form-label">Patient <span className="text-danger">*</span></label>
                  <Select
                    classNamePrefix="react-select"
                    options={patientOptions}
                    onChange={(opt) => setHeader((h) => ({ ...h, patientId: opt?.value || "", patientName: opt?.patientName || "" }))}
                    value={patientOptions.find((o) => o.value === header.patientId) || null}
                    placeholder="Search by name or mobile..."
                    isClearable
                    isSearchable
                  />
                </div>
                <div className="col-lg-4 mb-3">
                  <label className="form-label">Doctor</label>
                  <Select
                    classNamePrefix="react-select"
                    options={doctorOptions}
                    value={doctorOptions.find((o) => o.value === header.doctorId) || null}
                    onChange={(opt) => setHeader((h) => ({ ...h, doctorId: opt?.value || "" }))}
                    placeholder="Select Doctor"
                    isClearable
                  />
                </div>
                <div className="col-lg-3 mb-3">
                  <label className="form-label">Invoice Date</label>
                  <input type="date" className="form-control" value={header.invoiceDate}
                    onChange={(e) => setHeader((h) => ({ ...h, invoiceDate: e.target.value }))} />
                </div>
                <div className="col-lg-12 mb-2">
                  <label className="form-label">Remarks</label>
                  <input className="form-control" value={header.remarks}
                    onChange={(e) => setHeader((h) => ({ ...h, remarks: e.target.value }))}
                    placeholder="Optional notes" />
                </div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Services (Procedures)</h5>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={addService}>+ Add Row</button>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 220 }}>Procedure</th>
                      <th style={{ width: 80 }} className="text-center">Qty</th>
                      <th style={{ width: 110 }} className="text-end">Rate (₹)</th>
                      <th style={{ width: 90 }} className="text-center">Disc %</th>
                      <th style={{ width: 120 }} className="text-end">Amount (₹)</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <Select
                            classNamePrefix="react-select"
                            options={procedureOptions}
                            value={procedureOptions.find((o) => o.value === row.procedureId) || null}
                            onChange={(opt) => selectProcedure(idx, opt)}
                            placeholder="Select procedure"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                          />
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm text-center" min={1}
                            value={row.quantity} onChange={(e) => updateService(idx, "quantity", e.target.value)} />
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm text-end" min={0} step="0.01"
                            value={row.rate} onChange={(e) => updateService(idx, "rate", e.target.value)} />
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm text-center" min={0} max={100}
                            value={row.discount} onChange={(e) => updateService(idx, "discount", e.target.value)} />
                        </td>
                        <td className="text-end align-middle">
                          <strong>{fmt(calcLine(row.quantity, row.rate, row.discount))}</strong>
                        </td>
                        <td className="text-center align-middle">
                          {services.length > 1 && (
                            <button type="button" className="btn btn-sm text-danger p-1" onClick={() => removeService(idx)}>×</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-light">
                      <td colSpan={4} className="text-end fw-semibold">Services Total</td>
                      <td className="text-end fw-bold">₹{fmt(serviceTotal)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Products / Medicines</h5>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={addProduct}>+ Add Row</button>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 220 }}>Product</th>
                      <th style={{ width: 80 }} className="text-center">Qty</th>
                      <th style={{ width: 110 }} className="text-end">Rate (₹)</th>
                      <th style={{ width: 90 }} className="text-center">Disc %</th>
                      <th style={{ width: 120 }} className="text-end">Amount (₹)</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {productLines.length === 0 ? (
                      <tr><td colSpan={6} className="text-center text-muted py-2">No products added</td></tr>
                    ) : productLines.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <Select
                            classNamePrefix="react-select"
                            options={productOptions}
                            value={productOptions.find((o) => o.value === row.productId) || null}
                            onChange={(opt) => selectProduct(idx, opt)}
                            placeholder="Select product"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                          />
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm text-center" min={1}
                            value={row.quantity} onChange={(e) => updateProduct(idx, "quantity", e.target.value)} />
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm text-end" min={0} step="0.01"
                            value={row.rate} onChange={(e) => updateProduct(idx, "rate", e.target.value)} />
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm text-center" min={0} max={100}
                            value={row.discount} onChange={(e) => updateProduct(idx, "discount", e.target.value)} />
                        </td>
                        <td className="text-end align-middle">
                          <strong>{fmt(calcLine(row.quantity, row.rate, row.discount))}</strong>
                        </td>
                        <td className="text-center align-middle">
                          <button type="button" className="btn btn-sm text-danger p-1" onClick={() => removeProduct(idx)}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {productLines.length > 0 && (
                    <tfoot>
                      <tr className="table-light">
                        <td colSpan={4} className="text-end fw-semibold">Products Total</td>
                        <td className="text-end fw-bold">₹{fmt(productTotal)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>

          {/* Payments + Summary side-by-side */}
          <div className="row">
            <div className="col-lg-8">
              <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Payments Collected</h5>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addPayment}>+ Add Payment</button>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ minWidth: 150 }}>Mode</th>
                          <th style={{ width: 130 }} className="text-end">Amount (₹)</th>
                          <th style={{ width: 140 }}>Date</th>
                          <th>Reference #</th>
                          <th style={{ width: 50 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((row, idx) => (
                          <tr key={idx}>
                            <td>
                              <Select
                                classNamePrefix="react-select"
                                options={PAYMENT_MODES}
                                value={PAYMENT_MODES.find((o) => o.value === row.paymentMode) || null}
                                onChange={(opt) => updatePayment(idx, "paymentMode", opt?.value || "Cash")}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                              />
                            </td>
                            <td>
                              <input type="number" className="form-control form-control-sm text-end" min={0} step="0.01"
                                value={row.amount} onChange={(e) => updatePayment(idx, "amount", e.target.value)} />
                            </td>
                            <td>
                              <input type="date" className="form-control form-control-sm" value={row.paymentDate}
                                onChange={(e) => updatePayment(idx, "paymentDate", e.target.value)} />
                            </td>
                            <td>
                              <input className="form-control form-control-sm" value={row.referenceNo}
                                onChange={(e) => updatePayment(idx, "referenceNo", e.target.value)}
                                placeholder="Cheque/UTR/TxnId" />
                            </td>
                            <td className="text-center align-middle">
                              {payments.length > 1 && (
                                <button type="button" className="btn btn-sm text-danger p-1" onClick={() => removePayment(idx)}>×</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card mb-3">
                <div className="card-header"><h5 className="mb-0">Summary</h5></div>
                <div className="card-body">
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <td>Services</td>
                        <td className="text-end">₹{fmt(serviceTotal)}</td>
                      </tr>
                      <tr>
                        <td>Products</td>
                        <td className="text-end">₹{fmt(productTotal)}</td>
                      </tr>
                      <tr className="table-light">
                        <td className="fw-bold">Gross Total</td>
                        <td className="text-end fw-bold">₹{fmt(grossTotal)}</td>
                      </tr>
                      <tr>
                        <td>Paid</td>
                        <td className="text-end text-success fw-semibold">₹{fmt(paidTotal)}</td>
                      </tr>
                      <tr className={balance > 0 ? "table-danger" : "table-success"}>
                        <td className="fw-bold">Balance Due</td>
                        <td className={`text-end fw-bold ${balance > 0 ? "text-danger" : "text-success"}`}>
                          ₹{fmt(balance)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-end mb-4">
            <button type="button" className="btn btn-cancel" onClick={() => navigate("/invoices")}>Cancel</button>
            <button type="submit" className="btn btn-submit" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Update Invoice" : "Save Invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceForm;
